import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface AcademicPerformanceProps {
  domain: string;
}

interface StudentResult {
  id: string;
  studentId: string;
  studentName: string;
  className: string;
  section: string;
  examType: string;
  subjects: {
    name: string;
    marksObtained: number;
    maxMarks: number;
    grade: string;
  }[];
  totalMarks: number;
  maxTotalMarks: number;
  percentage: number;
  grade: string;
  rank?: number;
}

interface ClassPerformance {
  className: string;
  totalStudents: number;
  avgPercentage: number;
  passPercentage: number;
  topPerformer: string;
  topScore: number;
  gradeDistribution: { [grade: string]: number };
}

interface SubjectAnalysis {
  subject: string;
  avgMarks: number;
  maxMarks: number;
  avgPercentage: number;
  highestMarks: number;
  lowestMarks: number;
  passCount: number;
  failCount: number;
}

const AcademicPerformance: React.FC<AcademicPerformanceProps> = ({ domain }) => {
  const [results, setResults] = useState<StudentResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [selectedExam, setSelectedExam] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'class' | 'subject' | 'individual'>('class');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<StudentResult | null>(null);

  const examTypes = ['Mid Term', 'Final Term', 'Unit Test 1', 'Unit Test 2', 'Quarterly'];
  const subjects = ['Mathematics', 'Science', 'English', 'Hindi', 'Social Studies', 'Computer Science'];
  const grades = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D', 'F'];

  useEffect(() => {
    fetchResults();
  }, [domain]);

  const fetchResults = async () => {
    if (!domain) return;
    setLoading(true);

    try {
      const resultsRef = collection(db, `school-users/${domain}/results`);
      const snapshot = await getDocs(resultsRef);

      const resultsList: StudentResult[] = [];
      snapshot.forEach(doc => {
        resultsList.push({ id: doc.id, ...doc.data() } as StudentResult);
      });

      if (resultsList.length === 0) {
        setResults(generateSampleResults());
      } else {
        setResults(resultsList);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      setResults(generateSampleResults());
    } finally {
      setLoading(false);
    }
  };

  const generateSampleResults = (): StudentResult[] => {
    const classes = ['Class 6A', 'Class 6B', 'Class 7A', 'Class 7B', 'Class 8A', 'Class 8B',
                     'Class 9A', 'Class 9B', 'Class 10A', 'Class 10B'];
    const names = ['Aarav Sharma', 'Vivaan Patel', 'Aditya Singh', 'Vihaan Kumar', 'Arjun Reddy',
                   'Sai Gupta', 'Reyansh Verma', 'Ayaan Joshi', 'Ananya Iyer', 'Isha Rao',
                   'Saanvi Mehta', 'Aanya Nair', 'Pari Shah', 'Myra Das', 'Diya Kapoor', 'Kiara Bhat'];
    const results: StudentResult[] = [];

    classes.forEach(cls => {
      const studentsInClass = Math.floor(Math.random() * 10) + 25;

      for (let i = 0; i < studentsInClass; i++) {
        const studentName = names[Math.floor(Math.random() * names.length)];
        const studentId = `STU${cls.replace(/\s/g, '')}${String(i + 1).padStart(3, '0')}`;

        examTypes.forEach(exam => {
          const studentSubjects = subjects.map(sub => {
            const maxMarks = 100;
            // Generate marks with some distribution
            const baseScore = 40 + Math.floor(Math.random() * 50);
            const variation = Math.floor(Math.random() * 20) - 10;
            const marksObtained = Math.max(15, Math.min(100, baseScore + variation));
            const percentage = (marksObtained / maxMarks) * 100;

            let grade: string;
            if (percentage >= 90) grade = 'A+';
            else if (percentage >= 80) grade = 'A';
            else if (percentage >= 70) grade = 'B+';
            else if (percentage >= 60) grade = 'B';
            else if (percentage >= 50) grade = 'C+';
            else if (percentage >= 40) grade = 'C';
            else if (percentage >= 33) grade = 'D';
            else grade = 'F';

            return {
              name: sub,
              marksObtained,
              maxMarks,
              grade,
            };
          });

          const totalMarks = studentSubjects.reduce((sum, s) => sum + s.marksObtained, 0);
          const maxTotalMarks = studentSubjects.reduce((sum, s) => sum + s.maxMarks, 0);
          const percentage = Math.round((totalMarks / maxTotalMarks) * 100);

          let overallGrade: string;
          if (percentage >= 90) overallGrade = 'A+';
          else if (percentage >= 80) overallGrade = 'A';
          else if (percentage >= 70) overallGrade = 'B+';
          else if (percentage >= 60) overallGrade = 'B';
          else if (percentage >= 50) overallGrade = 'C+';
          else if (percentage >= 40) overallGrade = 'C';
          else if (percentage >= 33) overallGrade = 'D';
          else overallGrade = 'F';

          results.push({
            id: `${studentId}_${exam.replace(/\s/g, '')}`,
            studentId,
            studentName,
            className: cls,
            section: cls.slice(-1),
            examType: exam,
            subjects: studentSubjects,
            totalMarks,
            maxTotalMarks,
            percentage,
            grade: overallGrade,
          });
        });
      }
    });

    return results;
  };

  const uniqueClasses = [...new Set(results.map(r => r.className))].sort();
  const uniqueExams = [...new Set(results.map(r => r.examType))];

  const filteredResults = results.filter(r => {
    const matchesClass = selectedClass === 'all' || r.className === selectedClass;
    const matchesExam = selectedExam === 'all' || r.examType === selectedExam;
    const matchesSearch = searchTerm === '' ||
                         r.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         r.studentId.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesClass && matchesExam && matchesSearch;
  });

  // Calculate class-wise performance
  const classPerformance: ClassPerformance[] = uniqueClasses.map(cls => {
    const classResults = filteredResults.filter(r => r.className === cls);
    const uniqueStudents = new Set(classResults.map(r => r.studentId)).size;
    const avgPercentage = classResults.length > 0
      ? Math.round(classResults.reduce((sum, r) => sum + r.percentage, 0) / classResults.length)
      : 0;
    const passCount = classResults.filter(r => r.percentage >= 33).length;
    const passPercentage = classResults.length > 0 ? Math.round((passCount / classResults.length) * 100) : 0;

    const topResult = classResults.reduce((max, r) => r.percentage > max.percentage ? r : max,
                                          { percentage: 0, studentName: '-', totalMarks: 0 } as StudentResult);

    const gradeDistribution: { [grade: string]: number } = {};
    grades.forEach(g => { gradeDistribution[g] = 0; });
    classResults.forEach(r => {
      if (gradeDistribution[r.grade] !== undefined) {
        gradeDistribution[r.grade]++;
      }
    });

    return {
      className: cls,
      totalStudents: uniqueStudents,
      avgPercentage,
      passPercentage,
      topPerformer: topResult.studentName,
      topScore: topResult.percentage,
      gradeDistribution,
    };
  });

  // Calculate subject-wise analysis
  const subjectAnalysis: SubjectAnalysis[] = subjects.map(subject => {
    const subjectData = filteredResults.flatMap(r =>
      r.subjects.filter(s => s.name === subject)
    );

    if (subjectData.length === 0) {
      return {
        subject,
        avgMarks: 0,
        maxMarks: 100,
        avgPercentage: 0,
        highestMarks: 0,
        lowestMarks: 0,
        passCount: 0,
        failCount: 0,
      };
    }

    const avgMarks = Math.round(subjectData.reduce((sum, s) => sum + s.marksObtained, 0) / subjectData.length);
    const highestMarks = Math.max(...subjectData.map(s => s.marksObtained));
    const lowestMarks = Math.min(...subjectData.map(s => s.marksObtained));
    const passCount = subjectData.filter(s => s.marksObtained >= 33).length;

    return {
      subject,
      avgMarks,
      maxMarks: 100,
      avgPercentage: avgMarks,
      highestMarks,
      lowestMarks,
      passCount,
      failCount: subjectData.length - passCount,
    };
  });

  // Overall statistics
  const overallAvg = filteredResults.length > 0
    ? Math.round(filteredResults.reduce((sum, r) => sum + r.percentage, 0) / filteredResults.length)
    : 0;
  const totalPass = filteredResults.filter(r => r.percentage >= 33).length;
  const overallPassRate = filteredResults.length > 0
    ? Math.round((totalPass / filteredResults.length) * 100)
    : 0;
  const distinctionCount = filteredResults.filter(r => r.percentage >= 75).length;

  const getGradeColor = (grade: string): string => {
    switch (grade) {
      case 'A+': return '#28a745';
      case 'A': return '#20c997';
      case 'B+': return '#17a2b8';
      case 'B': return '#6f42c1';
      case 'C+': return '#ffc107';
      case 'C': return '#fd7e14';
      case 'D': return '#dc3545';
      case 'F': return '#6c757d';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100 bg-primary text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="small opacity-75">Average Score</div>
                  <div className="fs-3 fw-bold">{overallAvg}%</div>
                </div>
                <i className="bi bi-graph-up fs-2 opacity-50"></i>
              </div>
              <div className="small mt-2 opacity-75">
                {filteredResults.length} results analyzed
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="text-muted small">Pass Rate</div>
                  <div className="fs-3 fw-bold text-success">{overallPassRate}%</div>
                </div>
                <div className="rounded-circle bg-success bg-opacity-10 p-3">
                  <i className="bi bi-check-circle-fill text-success fs-4"></i>
                </div>
              </div>
              <div className="small text-muted mt-2">{totalPass} students passed</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="text-muted small">Distinction</div>
                  <div className="fs-3 fw-bold text-info">{distinctionCount}</div>
                </div>
                <div className="rounded-circle bg-info bg-opacity-10 p-3">
                  <i className="bi bi-award-fill text-info fs-4"></i>
                </div>
              </div>
              <div className="small text-muted mt-2">75%+ score</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="text-muted small">Total Classes</div>
                  <div className="fs-3 fw-bold text-primary">{uniqueClasses.length}</div>
                </div>
                <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                  <i className="bi bi-building text-primary fs-4"></i>
                </div>
              </div>
              <div className="small text-muted mt-2">Analyzed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label small text-muted">Class</label>
              <select
                className="form-select"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <option value="all">All Classes</option>
                {uniqueClasses.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small text-muted">Exam</label>
              <select
                className="form-select"
                value={selectedExam}
                onChange={(e) => setSelectedExam(e.target.value)}
              >
                <option value="all">All Exams</option>
                {uniqueExams.map(exam => (
                  <option key={exam} value={exam}>{exam}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted">Search Student</label>
              <input
                type="text"
                className="form-control"
                placeholder="Name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-5">
              <div className="btn-group w-100">
                <button
                  className={`btn ${viewMode === 'class' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('class')}
                >
                  <i className="bi bi-building me-1"></i>Class-wise
                </button>
                <button
                  className={`btn ${viewMode === 'subject' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('subject')}
                >
                  <i className="bi bi-book me-1"></i>Subject-wise
                </button>
                <button
                  className={`btn ${viewMode === 'individual' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('individual')}
                >
                  <i className="bi bi-person me-1"></i>Individual
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'class' ? (
        /* Class-wise Performance */
        <div className="row g-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white py-3">
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-building me-2 text-primary"></i>
                  Class-wise Performance
                </h6>
              </div>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Class</th>
                      <th className="text-center">Students</th>
                      <th className="text-center">Avg %</th>
                      <th className="text-center">Pass Rate</th>
                      <th>Top Performer</th>
                      <th>Grade Distribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classPerformance.map(cls => (
                      <tr key={cls.className}>
                        <td className="fw-semibold">{cls.className}</td>
                        <td className="text-center">{cls.totalStudents}</td>
                        <td className="text-center">
                          <span className={`badge ${cls.avgPercentage >= 60 ? 'bg-success' : cls.avgPercentage >= 40 ? 'bg-warning' : 'bg-danger'}`}>
                            {cls.avgPercentage}%
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center">
                            <div className="progress me-2" style={{ width: 60, height: 6 }}>
                              <div
                                className={`progress-bar ${cls.passPercentage >= 80 ? 'bg-success' : cls.passPercentage >= 50 ? 'bg-warning' : 'bg-danger'}`}
                                style={{ width: `${cls.passPercentage}%` }}
                              ></div>
                            </div>
                            <small>{cls.passPercentage}%</small>
                          </div>
                        </td>
                        <td>
                          <span className="fw-semibold">{cls.topPerformer}</span>
                          <span className="badge bg-success ms-2">{cls.topScore}%</span>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            {grades.slice(0, 5).map(grade => (
                              <span
                                key={grade}
                                className="badge"
                                style={{ backgroundColor: getGradeColor(grade), minWidth: 30 }}
                                title={`${grade}: ${cls.gradeDistribution[grade] || 0}`}
                              >
                                {cls.gradeDistribution[grade] || 0}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : viewMode === 'subject' ? (
        /* Subject-wise Analysis */
        <div className="row g-3">
          {subjectAnalysis.map(subject => (
            <div key={subject.subject} className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h6 className="fw-bold mb-3">
                    <i className="bi bi-book me-2 text-primary"></i>
                    {subject.subject}
                  </h6>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="small text-muted">Average Score</span>
                      <span className="fw-bold">{subject.avgMarks}%</span>
                    </div>
                    <div className="progress" style={{ height: 10 }}>
                      <div
                        className={`progress-bar ${subject.avgMarks >= 60 ? 'bg-success' : subject.avgMarks >= 40 ? 'bg-warning' : 'bg-danger'}`}
                        style={{ width: `${subject.avgMarks}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="row g-2 small">
                    <div className="col-6">
                      <div className="p-2 bg-light rounded text-center">
                        <div className="text-success fw-bold">{subject.highestMarks}</div>
                        <div className="text-muted">Highest</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="p-2 bg-light rounded text-center">
                        <div className="text-danger fw-bold">{subject.lowestMarks}</div>
                        <div className="text-muted">Lowest</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="p-2 bg-light rounded text-center">
                        <div className="text-primary fw-bold">{subject.passCount}</div>
                        <div className="text-muted">Passed</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="p-2 bg-light rounded text-center">
                        <div className="text-warning fw-bold">{subject.failCount}</div>
                        <div className="text-muted">Failed</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Individual Student View */
        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-people me-2"></i>
                  Student Results ({filteredResults.length})
                </h6>
                <button className="btn btn-sm btn-outline-success">
                  <i className="bi bi-download me-2"></i>Export
                </button>
              </div>
              <div className="table-responsive" style={{ maxHeight: '500px' }}>
                <table className="table table-hover table-sm mb-0">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Student</th>
                      <th>Class</th>
                      <th>Exam</th>
                      <th className="text-center">Score</th>
                      <th className="text-center">Grade</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults
                      .sort((a, b) => b.percentage - a.percentage)
                      .slice(0, 100)
                      .map((result, idx) => (
                        <tr
                          key={result.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedStudent(result)}
                          className={selectedStudent?.id === result.id ? 'table-active' : ''}
                        >
                          <td>
                            <div className="d-flex align-items-center">
                              <span className="badge bg-light text-dark me-2">{idx + 1}</span>
                              <div>
                                <div className="fw-semibold">{result.studentName}</div>
                                <small className="text-muted">{result.studentId}</small>
                              </div>
                            </div>
                          </td>
                          <td>{result.className}</td>
                          <td>{result.examType}</td>
                          <td className="text-center">
                            <span className={`fw-bold ${result.percentage >= 60 ? 'text-success' : result.percentage >= 33 ? 'text-warning' : 'text-danger'}`}>
                              {result.percentage}%
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge" style={{ backgroundColor: getGradeColor(result.grade) }}>
                              {result.grade}
                            </span>
                          </td>
                          <td>
                            <i className="bi bi-chevron-right text-muted"></i>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Student Detail Panel */}
          <div className="col-lg-4">
            {selectedStudent ? (
              <div className="card border-0 shadow-sm sticky-top" style={{ top: '1rem' }}>
                <div className="card-header bg-primary text-white py-3">
                  <h6 className="mb-0 fw-bold">{selectedStudent.studentName}</h6>
                  <small className="opacity-75">{selectedStudent.studentId} | {selectedStudent.className}</small>
                </div>
                <div className="card-body">
                  <div className="text-center mb-4">
                    <div className="display-4 fw-bold" style={{ color: getGradeColor(selectedStudent.grade) }}>
                      {selectedStudent.percentage}%
                    </div>
                    <span className="badge fs-6" style={{ backgroundColor: getGradeColor(selectedStudent.grade) }}>
                      Grade {selectedStudent.grade}
                    </span>
                    <div className="small text-muted mt-2">
                      {selectedStudent.totalMarks} / {selectedStudent.maxTotalMarks} marks
                    </div>
                  </div>

                  <h6 className="fw-bold mb-3">Subject-wise Marks</h6>
                  {selectedStudent.subjects.map(subject => (
                    <div key={subject.name} className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="small">{subject.name}</span>
                        <span className="small fw-bold">
                          {subject.marksObtained}/{subject.maxMarks}
                          <span
                            className="badge ms-2"
                            style={{ backgroundColor: getGradeColor(subject.grade), fontSize: '0.65rem' }}
                          >
                            {subject.grade}
                          </span>
                        </span>
                      </div>
                      <div className="progress" style={{ height: 6 }}>
                        <div
                          className="progress-bar"
                          style={{
                            width: `${(subject.marksObtained / subject.maxMarks) * 100}%`,
                            backgroundColor: getGradeColor(subject.grade),
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center text-muted py-5">
                  <i className="bi bi-person-circle fs-1 d-block mb-3"></i>
                  <p className="mb-0">Select a student to view detailed results</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicPerformance;
